<template>
  <div id="tactics-board">
    <TheFormationSelect />

    <div id="field-container">
      <div id="field-outline">
        <div id="centre-circle" />
        <div id="halfway-line" />
        <div class="box penalty-circle pc--home" />
        <div class="box penalty-box box--home" />
        <div class="box six-yard-box box--home" />
        <div class="box penalty-box box--away" />
        <div class="box six-yard-box box--away" />
        <div class="box penalty-circle pc--away" />
      </div>

      <svg
        id="partnership-visualisation"
        ref="partnershipVisualisation"
        width="100%"
        height="100%"
      >
        <template v-for="(zonePartnerships, zone) in partnerships">
          <PartnershipVisualisation
            v-for="(partnership, index) in zonePartnerships"
            :key="zone + index"
            :zone="zone"
            :partnership="partnership"
            :partner1="$refs['player' + partnership.partner1]"
            :partner2="$refs['player' + partnership.partner2]"
          />
        </template>
      </svg>

      <TacticsBoardPosition
        v-for="(position, key) in positions"
        :key="key"
        :ref="'player' + position.positionNumber"
        :position="position"
        :selectedRole="positions[key].selectedRole"
        :dragging="dragging"
        :selected="position === selectedPosition"
        @update:dragging="dragging = $event"
        @update:selected-position="selectedPosition = $event"
        @touch-move-ended="onTouchMoveEnded"
      />
    </div>

    <PositionSummary
      v-if="selectedPosition !== null"
      :position="selectedPosition"
      @update:selected-position="selectedPosition = $event"
    />
  </div>
</template>

<script>
import TheFormationSelect from "@/components/TheFormationSelect";
import TacticsBoardPosition from "@/components/TacticsBoardPosition.vue";
import PositionSummary from "./PositionSummary";
import positionData from "@/assets/js/position-data";
import PartnershipVisualisation from "@/components/PartnershipVisualisation";

export default {
  name: "TheTacticsBoard",

  components: {
    TheFormationSelect,
    TacticsBoardPosition,
    PositionSummary,
    PartnershipVisualisation,
  },

  data: function () {
    let positions = {};
    const c = Object.keys(positionData).length;

    for (let i = 1; i <= c; i++) {
      const key = "player" + i;
      positions[key] = {
        ...positionData[key],
      };
    }

    const playerNameData = [
      { name: "RIXON", number: 1, preferredPosition: 1 },
      { name: "AIRTON", number: 3, preferredPosition: 6 },
      { name: "GETZ", number: 2, preferredPosition: null },
      { name: "MCFLY", number: 4, preferredPosition: null },
      { name: "KUGAWA", number: 5, preferredPosition: null },
      { name: "BRADBURY", number: 6, preferredPosition: null },
      { name: "KEY", number: 7, preferredPosition: null },
      { name: "DUNN", number: 8, preferredPosition: null },
      { name: "JOHNSTON", number: 9, preferredPosition: null },
      { name: "TAN", number: 10, preferredPosition: null },
      { name: "DI NICOLA", number: 11, preferredPosition: null },
    ];

    return {
      dragging: null,
      partnershipCurves: {},
      gkIcon: require("@/assets/img/gk.png"),
      shirtIcon: require("@/assets/img/shirt.png"),
      positionData: positions,
      playerNameData,
      selectedPosition: null,
      production: process.env.NODE_ENV !== "development",
    };
  },

  computed: {
    positions() {
      let positions = {};
      const gkPlayer = this.playerNameData.find((shirt) => shirt.number === 1);
      let outfieldPlayersWithPreferredPosition = this.playerNameData.filter(
        (shirt) => shirt.number !== 1 && shirt.preferredPosition
      );
      const outfieldPlayersNoPreferredPosition = this.playerNameData.filter(
        (shirt) => shirt.number !== 1 && !shirt.preferredPosition
      );

      for (const [key, value] of Object.entries(this.$store.state.positions)) {
        positions[key] = {
          ...this.positionData[key],
          ...value,
        };
      }

      for (const [key, value] of Object.entries(positions)) {
        if (!value.filled) continue;
        if (value.positionNumber === 1) {
          positions[key].shirtName = gkPlayer.name;
          positions[key].shirtNumber = gkPlayer.number;
          continue;
        }

        const preferredPosition = outfieldPlayersWithPreferredPosition.find(
          (player) => player.preferredPosition === positions[key].positionNumber
        );
        outfieldPlayersWithPreferredPosition =
          outfieldPlayersWithPreferredPosition.filter(
            (player) =>
              player.preferredPosition !== positions[key].positionNumber
          );
        if (preferredPosition) {
          positions[key].shirtName = preferredPosition.name;
          positions[key].shirtNumber = preferredPosition.number;
        }
      }

      const unassignedShirts = [
        ...outfieldPlayersWithPreferredPosition,
        ...outfieldPlayersNoPreferredPosition,
      ];

      for (const [key, value] of Object.entries(positions)) {
        if (!value.filled) continue;
        if (positions[key].shirtName) continue;

        const player = unassignedShirts.shift();
        positions[key].shirtName = player.name;
        positions[key].shirtNumber = player.number;
      }

      return positions;
    },

    partnerships() {
      return this.$store.getters.partnerships;
    },

    console: () => console,
  },

  methods: {
    onTouchMoveEnded(e) {
      for (const positionName of Object.keys(this.positions)) {
        const ref = this.$refs[positionName];
        const boundingBox = ref[0].$el.getBoundingClientRect();
        const intersect =
          e.changedTouches[0].clientX >= boundingBox.left &&
          e.changedTouches[0].clientX <= boundingBox.right &&
          e.changedTouches[0].clientY >= boundingBox.top &&
          e.changedTouches[0].clientY <= boundingBox.bottom;
        if (intersect) {
          if (this.positions[positionName].filled) break;

          this.$store.commit("setPositionFilled", {
            position: positionName,
            filled: true,
          });
          this.$store.commit("setPositionFilled", {
            position: "player" + this.dragging.positionNumber,
            filled: false,
          });

          break;
        }
      }

      this.dragging = null;
    },
  },
};
</script>

<style scoped>
.am-row {
  grid-row-start: 2;
}
.m-row {
  grid-row-start: 3;
}
.dm-row {
  grid-row-start: 4;
}
.d-row {
  grid-row-start: 5;
}
.gk-row {
  grid-row-start: 6;
}

#partnership-visualisation {
  position: absolute;
}

#field-container {
  width: calc(54vw - 198px);
  max-width: 70vh;
  min-width: 472px;
  height: 50vw;
  max-height: 90vh;
  min-height: 563px;
  background: #167e45;
  margin: 18px 0 73px;
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  justify-items: center;
  justify-content: center;
  grid-gap: 4px;
  box-sizing: border-box;
  position: relative;
}

#field-outline {
  opacity: 0.5;
  border: solid 1px #fff;
  height: 90%;
  width: 90%;
  position: absolute;
  transform: perspective(5cm) rotateX(2deg);
}

#centre-circle {
  opacity: 0.5;
  border: solid 1px #fff;
  height: 5vw;
  width: 5vw;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  border-radius: 50%;
}

#halfway-line {
  opacity: 0.5;
  background: #fff;
  height: 1px;
  width: 100%;
  top: 50%;
  position: absolute;
}

.box {
  opacity: 0.5;
  border: solid 1px #fff;
  left: 50%;
  transform: translate(-50%, 0);
  position: absolute;
}

.penalty-box {
  height: 8vw;
  width: 20vw;
}

.six-yard-box {
  height: 4vw;
  width: 10vw;
}

.box--home {
  top: 0;
}

.box--away {
  bottom: 0;
}

.penalty-circle {
  opacity: 0.5;
  border: solid 1px #fff;
  height: 5vw;
  width: 5vw;
  position: absolute;
  border-radius: 50%;
}

.pc--home {
  clip-path: polygon(0 67%, 100% 67%, 100% 100%, 0 100%);
  top: 4.7vw;
}

.pc--away {
  clip-path: polygon(0 0, 100% 0, 100% 33%, 0 33%);
  bottom: 4.6vw;
}

#touch-end-marker {
  position: fixed;
  height: 20px;
  width: 20px;
  background: magenta;
  transform: translate(-50%, -50%);
}

@media only screen and (max-width: 1339px) {
  #field-container {
    width: 73vw;
    height: 93vw;
  }

  .penalty-box {
    height: 18vw;
    width: 36vw;
    max-width: 36vh;
    max-height: 18vh;
  }

  .six-yard-box {
    height: 8vw;
    width: 16vw;
    max-width: 16vh;
    max-height: 8vh;
  }

  #centre-circle {
    height: 15vw;
    width: 15vw;
    max-width: 15vh;
    max-height: 15vh;
  }

  .penalty-circle {
    height: 15vw;
    width: 15vw;
    max-width: 12vh;
    max-height: 12vh;
  }

  .pc--home {
    top: 8vw;
  }

  .pc--away {
    bottom: 8vw;
  }
}

@media only screen and (max-width: 767px) {
  #field-container {
    width: 90vw;
    height: 110vw;
    min-width: unset;
    padding: 0;
  }

  .pc--home {
    top: 8vw;
  }

  .pc--away {
    bottom: 8vw;
  }

  .mobile-hide {
    display: none !important;
  }
}
</style>
